import React, { useState, useEffect } from 'react';
import { Shield, Key, Lock, RefreshCw, Check, AlertTriangle, Eye, EyeOff, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';

const SecuritySettings = () => {
  const { user, updatePassword } = useAuth();
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => { loadLogs(); }, [user]);

  const loadLogs = async () => {
    if (!user) return;
    setLogsLoading(true);
    try {
      const { data } = await supabase.from('audit_logs').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20);
      setLogs(data || []);
    } catch (e) {}
    setLogsLoading(false);
  };

  const validatePassword = (pass) => {
    const errors = [];
    if (pass.length < 8) errors.push('8+ chars');
    if (!/[A-Z]/.test(pass)) errors.push('uppercase');
    if (!/[a-z]/.test(pass)) errors.push('lowercase');
    if (!/[0-9]/.test(pass)) errors.push('number');
    if (!/[^A-Za-z0-9]/.test(pass)) errors.push('special char');
    return errors;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(''); setPasswordSuccess('');
    const errors = validatePassword(newPassword);
    if (errors.length > 0) { setPasswordError(`Need: ${errors.join(', ')}`); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    setPasswordLoading(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) throw error;
      setPasswordSuccess('Password updated!');
      setNewPassword(''); setConfirmPassword(''); setShowPasswordChange(false);
    } catch (err) { setPasswordError(err.message); }
    setPasswordLoading(false);
  };

  const formatDate = (d) => new Date(d).toLocaleString();
  const formatAction = (a) => a.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Lock className="w-5 h-5" /> Password</h2>
        {!showPasswordChange ? (
          <button onClick={() => setShowPasswordChange(true)} className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600">Change Password</button>
        ) : (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {passwordError && <div className="p-3 bg-red-900/50 border border-red-500 rounded-lg text-sm text-red-200">{passwordError}</div>}
            {passwordSuccess && <div className="p-3 bg-green-900/50 border border-green-500 rounded-lg text-sm text-green-200">{passwordSuccess}</div>}
            <div>
              <label className="text-sm text-gray-400 block mb-1">New Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-2 bg-gray-700 rounded-lg pr-10" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              {newPassword && validatePassword(newPassword).length > 0 && <p className="text-xs text-yellow-400 mt-1">Need: {validatePassword(newPassword).join(', ')}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Confirm Password</label>
              <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full px-4 py-2 bg-gray-700 rounded-lg ${confirmPassword && newPassword !== confirmPassword ? 'ring-2 ring-red-500' : ''}`} required />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setShowPasswordChange(false); setNewPassword(''); setConfirmPassword(''); setPasswordError(''); }} className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600">Cancel</button>
              <button type="submit" disabled={passwordLoading} className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2">{passwordLoading && <RefreshCw className="w-4 h-4 animate-spin" />}Update</button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Key className="w-5 h-5" /> Account</h2>
        <div className="space-y-2 text-sm">
          <p><span className="text-gray-400">Email:</span> {user?.email}</p>
          <p><span className="text-gray-400">User ID:</span> <code className="text-xs bg-gray-700 px-1 rounded">{user?.id}</code></p>
          <p><span className="text-gray-400">Last Sign In:</span> {user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'N/A'}</p>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Activity Log</h2>
          <button onClick={loadLogs} disabled={logsLoading} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600"><RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} /></button>
        </div>
        {logsLoading ? (
          <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-500" /></div>
        ) : logs.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No activity yet</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {logs.map(log => (
              <div key={log.id} className="flex items-center justify-between p-2 bg-gray-700/50 rounded">
                <span className="text-sm">{formatAction(log.action)}</span>
                <span className="text-xs text-gray-500">{formatDate(log.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
